<template>
  <div :id="`heatmap-${_uid}`" class="scatterplot" :layout="responsive">
    <div class="visualization-controls">
      <b-row>
        <b-col>
          <b-form-checkbox
            v-model="showLabels"
            switch
            size="sm"
            class="my-4"
          >
            Show labels
          </b-form-checkbox>
        </b-col>
        <b-col>
          <b-form-checkbox
            v-model="showRecency"
            switch
            size="sm"
            class="my-4"
          >
            Show Recency
          </b-form-checkbox>
        </b-col>
        <b-col>
          <label for="range-1" class="mr-1">Neighbours: <b>{{nbors}}</b></label>
          <b-form-input id="range-1" v-model="nbors" type="range" min="1" max="15"></b-form-input>
        </b-col>
        <b-col>
          <label for="range-2" class="mr-1">Clusters: <b>{{clstrs}}</b></label>
          <b-form-input id="range-2" v-model="clstrs" type="range" min="1" max="10"></b-form-input>
        </b-col>
        <b-col>
          <b-button
              class="ml-auto mt-3"
              variant="accent"
              :disabled="nbors === neighbours && clstrs === clusters"
              @click="reDraw()"
            >
          Plot Again
          </b-button>
        </b-col>
      </b-row>
    </div>
    <div id="svgDiv">
      <div id="load-div-heatmap" class="loading-visualization">
        <div class="d-flex justify-content-center">
          <b-spinner
            style="width: 5rem; height: 5rem; margin-top:20%"
            variant="secondary-orange"
            label="Large Spinner"
            type="grow"
          ></b-spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import { dataForDimRed, calculateSR } from "../Visualizations/shared/utilities";
import SimplePlot from "./SimplePlot";
import GeneralChart from "./GeneralChart";

export default {
  name: "DimredPlotView",
  components: {
    dataForDimRed,
    calculateSR
  },
  props: {
    analyse: {
      required: true,
      type: Object
    },
    settings: {
      required: false,
      type: Object,
      default: null
    },
    responsive: {
      required: true,
      type: Boolean
    }
  },
  data() {
    return {
      crainData: null,
      cmData: null,
      query: null,
      visualization: null,
      showLabels: true,
      showRecency: false,
      nbors: 0,
      clstrs: 0,
      dimRedRawData: null,
      dimRedData: null,
      neighbours: 0,
      clusters: 0
    };
  },
  computed: {
    ...mapState({
      filterFocused: state => state.filter.filterFocused,
    })
  },
  watch: {
    analyse(){
      this.initAnalysis();
    },
    showLabels(){
      this.reDraw();
    },
    showRecency(){
      this.reDraw();
    }
  },
  mounted() {
    this.initAnalysis();
  },
  methods: {
    ...mapActions(["reduceDimensions","getItemData"]),
    async initAnalysis(){
      this.dimRedRawData = null;
      this.query = { ...this.analyse.config.query };
      if(this.query.postprocessing.length === 0){
        this.query.postprocessing.push("clustering");
      }
      this.query.subset_filters = this.filterFocused
        ? [this.filterFocused.rawquery]
        : [];

      this.visualization = new GeneralChart(
        `#heatmap-${this._uid} svg`,
        "heatmap",
        this.settings
      );
      this.visualization.setQuery(this.query);
      await this.visualization.init(null);
      this.crainData = this.visualization.control.crainData;
      //console.log(this.crainData);
      this.dimReduction();
    },
    drawClusterMap(reset = true){
      //console.log(this.dimRedData);
      this.nbors = this.neighbours;
      this.clstrs = this.clusters;
      this.cmData = this.dimRedData;
      this.visualization = new SimplePlot();
      this.visualization.control.crainData = this.cmData; 
      this.visualization.plotData(`#heatmap-${this._uid} #svgDiv`, this.cmData, this.clusters, this.neighbours, this.showLabels, this.showRecency, reset);
    },
    reDraw(){
      this.dimReduction([this.clstrs, this.nbors]);
    },
    async dimReduction(params = []) {
      this.dimRedData = null;
      let dimredData = [];
      if(!this.dimRedRawData){
        this.dimRedRawData = {
          cd: this.crainData,
          drData: this.crainData.rawData.dimension_reduction,
          colSize: this.crainData.xLayers.length,
          rowSize: this.crainData.yLayers.length,
          cols: this.crainData.xLayers,
          rows: this.crainData.yLayers,
          //srVals: await calculateSR(this.$refs.anl.visualization.control.crainData.yLayers)
          srVals: await calculateSR(this.analyse.config.query.ylayers, this.analyse.config.query.recentyear, this.analyse.config.query.startyear, this.analyse.config.query.stopyear)
        }
      }
      //console.log(this.dimRedRawData.srVals);
      //console.log(this.dimRedRawData.cd);
      dataForDimRed(this.dimRedRawData.drData, this.dimRedRawData.rowSize, this.dimRedRawData.colSize).then(res => {
        let nbors = params[1]? parseInt(params[1]):Math.round(Math.sqrt(res.length));
        let clusters = params[0]? parseInt(params[0]):6;
        this.reduceDimensions({dimensions:res, n: nbors, c: clusters}).then(result => {
            let dmData = JSON.parse(result);
            //console.log(dmData);
            let x = Object.values(dmData.x);
            let y = Object.values(dmData.y);
            let cl = Object.values(dmData.clusterlabels);
            this.clusters = clusters;
            this.neighbours = nbors;
            let sr = this.dimRedRawData.srVals;
            //console.log(sr);
            y.forEach((r, n) => {
              //console.log(this.dimRedRawData.rows[n][0]);
              let rsr = sr.find(i => { return parseInt(i.place) === n})
              dimredData.push({item:this.dimRedRawData.rows[n][0], n: n+1, x: r, y: x[n], cluster:cl[n], label: this.dimRedRawData.rows[n][0].name || null, size: rsr.values[0] || 0, recency: rsr.values[2]})
              if((n+1) === this.dimRedRawData.rows.length){
                this.dimRedData = dimredData;
                this.drawClusterMap();
              }
            })
        })
        .catch(error => {
          console.log(error);
          document.getElementById("load-div-heatmap").classList.remove("show");
        });
      })
    },
  },
  destroyed() {
    this.visualization.destroy();
  }
};
</script>
